import { useMemo, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Icon } from '@factorialco/f0-react'
import { Widget } from '@factorialco/f0-react/dist/experimental'
import { ChevronDown, ChevronUp, Pencil } from '@factorialco/f0-react/icons/app'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency, { isCurrency } from 'modules/core/lib/money'

import { CustomTwoColumnsList } from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/CustomTwoColumnList'
import BreakdownDayRow from 'modules/expenses/components/expenses/New/PerDiemWizard/components/BreakdownDayRow'
import { buildConceptExclusionsByDate } from 'modules/expenses/components/expenses/New/PerDiemWizard/companyPaidUtils'
import { formatDateRange } from 'modules/expenses/components/expenses/New/PerDiemWizard/generateDefaultTripName'
import { usePerDiemBreakdown } from 'modules/expenses/components/expenses/New/PerDiemWizard/hooks/usePerDiemBreakdown'
import { Expensable } from 'modules/expenses/types/Expensable'

interface PerDiemDetailsWidgetProps {
  expensable: Expensable
  editable: boolean
  onEditRoutes: () => void
  onEditDeductions: () => void
}

const InlineEditButton = ({ onClick }: { onClick: () => void }) => (
  <Box
    width="fit-content"
    paddingX="s8"
    paddingY="s1"
    borderRadius={{ all: 'abs008' }}
    style={{
      border: '1px solid hsla(213, 87%, 15%, 0.14)',
      cursor: 'pointer',
    }}
    onClick={onClick}
  >
    <Box flexDirection="row" alignItems="center" gap="s2">
      <Box width="s24" height="s24" alignItems="center" justifyContent="center">
        <F0Icon icon={Pencil} size="sm" />
      </Box>
      <Text size="200" weight="semibold">
        {i18n.t('expenses_v2.confirmation_screen.confirmation_screen_block_edit_action')}
      </Text>
    </Box>
  </Box>
)

const PerDiemDetailsWidget = ({
  expensable,
  editable,
  onEditRoutes,
  onEditDeductions,
}: PerDiemDetailsWidgetProps) => {
  const {
    employee,
    access: { locale },
  } = useCurrentSession()

  const [expanded, setExpanded] = useState(false)

  const perDiem = expensable?.perDiem
  const hasConcepts = (perDiem?.perDiemConcepts?.length ?? 0) > 0
  const hasDateRange = Boolean(perDiem?.startDate && perDiem?.endDate)
  const hasDestinations = (perDiem?.perDiemDestinations?.length ?? 0) > 0
  const hasStartDate = Boolean(perDiem?.startDate)

  const canFetchBreakdown = hasDestinations && hasStartDate

  const { days, isLoading, totalAmountCents, currency } = usePerDiemBreakdown({
    expensable: canFetchBreakdown ? expensable : undefined,
    employeeId: canFetchBreakdown ? employee?.id : undefined,
  })

  // Compute date range text with duration
  const dateRangeText = useMemo(() => {
    if (!perDiem?.startDate || !perDiem?.endDate) return null

    const startDate = new Date(perDiem.startDate)
    const endDate = new Date(perDiem.endDate)
    const dateRange = formatDateRange(startDate, endDate)

    const start = moment(startDate)
    const end = moment(endDate)
    const tripDays = end.diff(start, 'days') + 1
    const tripNights = Math.max(0, tripDays - 1)

    const daysLabel = i18n.tp('expenses_v2.forms.germany_per_diem_modal.trip_details.days', {
      count: tripDays,
    })
    const nightsLabel = i18n.tp('expenses_v2.forms.germany_per_diem_modal.trip_details.nights', {
      count: tripNights,
    })
    const durationStr = i18n.t(
      'expenses_v2.forms.germany_per_diem_modal.steps.step3.date_range_duration',
      {
        days: tripDays,
        daysLabel,
        nights: tripNights,
        nightsLabel,
      }
    )

    return `${dateRange} ${durationStr}`
  }, [perDiem?.startDate, perDiem?.endDate])

  // Count company-paid deductions from per diem concepts
  const companyPaidDeductionsCount = useMemo(() => {
    if (!perDiem?.perDiemConcepts) return 0
    return perDiem.perDiemConcepts.reduce((count, concept) => {
      const exclusionCount =
        (concept.breakfastProvided ? 1 : 0) +
        (concept.lunchProvided ? 1 : 0) +
        (concept.dinnerProvided ? 1 : 0) +
        (concept.accommodationProvided ? 1 : 0)
      return count + exclusionCount
    }, 0)
  }, [perDiem?.perDiemConcepts])

  const conceptExclusionsByDate = useMemo(
    () => buildConceptExclusionsByDate(perDiem?.perDiemConcepts),
    [perDiem?.perDiemConcepts]
  )

  if (!hasConcepts && !hasDateRange && !canFetchBreakdown) {
    return null
  }

  const formattedTotal =
    canFetchBreakdown && isCurrency(currency)
      ? MoneyWithCurrency({ amount: totalAmountCents, currency, locale })
      : ''

  const deductionsText = i18n.tp(
    'expenses_v2.forms.germany_per_diem_modal.steps.step3.company_paid_deductions',
    { count: companyPaidDeductionsCount }
  )

  // Build summary list rows (all in one list for consistent spacing)
  const summaryList = [
    // Routes & Dates row (with inline edit button when editable)
    ...(dateRangeText
      ? [
          {
            title: i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.routes_and_dates'),
            info: dateRangeText,
            customInfoBlock: (
              <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween" gap="s8">
                <Text size="200" weight="semibold" color="grey1000" whiteSpace="breakSpaces">
                  {dateRangeText}
                </Text>
                {editable && <InlineEditButton onClick={onEditRoutes} />}
              </Box>
            ),
          },
        ]
      : []),
    // Deductions row (with inline edit button when editable)
    {
      title: i18n.t('expenses_v2.forms.germany_per_diem_modal.breakdown.deductions'),
      info: deductionsText,
      customInfoBlock: (
        <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween" gap="s8">
          <Text size="200" weight="semibold" color="grey1000" whiteSpace="breakSpaces">
            {deductionsText}
          </Text>
          {editable && <InlineEditButton onClick={onEditDeductions} />}
        </Box>
      ),
    },
    // Total to reimburse row
    ...(formattedTotal
      ? [
          {
            title: i18n.t(
              'expenses_v2.confirmation_screen.expense.per_diem_rates_block.total_to_reimburse'
            ),
            info: formattedTotal,
          },
        ]
      : []),
  ]

  const widgetTitle = i18n.t('expenses_v2.confirmation_screen.expense.per_diem_summary_block.title')

  return (
    <Box gap="s16" position="relative">
      <Widget
        header={{
          title: widgetTitle,
        }}
      >
        <Box flexDirection="column" gap="s16">
          {isLoading ? (
            <Text.Normal size="200" color="grey600">
              {i18n.t('expenses_v2.forms.germany_per_diem_modal.breakdown.loading')}
            </Text.Normal>
          ) : (
            <>
              <CustomTwoColumnsList list={summaryList} />

              {canFetchBreakdown && (
                <>
                  <Box
                    onClick={() => setExpanded((prev) => !prev)}
                    flexDirection="row"
                    justifyContent="spaceBetween"
                    alignItems="center"
                    style={{ cursor: 'pointer' }}
                  >
                    <Text.Normal size="200" weight="medium" color="grey1000">
                      {expanded
                        ? i18n.t(
                            'expenses_v2.forms.germany_per_diem_modal.breakdown.hide_full_breakdown'
                          )
                        : i18n.t(
                            'expenses_v2.forms.germany_per_diem_modal.breakdown.see_full_breakdown'
                          )}
                    </Text.Normal>
                    <F0Icon size="lg" icon={expanded ? ChevronUp : ChevronDown} />
                  </Box>

                  {expanded && days && days.length > 0 && (
                    <Box flexDirection="column" gap="s24">
                      {days
                        .filter(
                          (day): day is NonNullable<typeof day> => day !== null && day !== undefined
                        )
                        .map((day) => (
                          <BreakdownDayRow
                            key={day.date}
                            day={day}
                            currency={currency}
                            locale={locale}
                            conceptExclusions={conceptExclusionsByDate?.[day.date]}
                          />
                        ))}
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Widget>
    </Box>
  )
}

export default PerDiemDetailsWidget
